import { Expose, Type, Exclude, classToPlain } from 'class-transformer';
import { Feature, Point } from 'geojson';

import { ExposeRawData } from '@lib/common/decorators';
import { SnModel } from '@lib/common/models';

import type { SnCamera } from '../camera';

import { SnContentType } from '../base';
import { SnShareInfo } from '../share';
import { SnTeam } from '../team';
import { SnUser } from '../user';
import { SnRecordingUrls } from './recording-urls';

export class SnRecording extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  public deviceOrientation: string;

  @Expose()
  public batteryStart: number;

  @Expose()
  public batteryEnd: number;

  @Expose()
  public address: string;

  @Expose()
  public briefcamReviews: any;

  @Expose()
  public city: string;

  @Expose()
  public meta: any;

  @Expose()
  public state: string;

  @Expose()
  public zip: string;

  @Expose()
  public organization: any;

  @Expose({ name: 'vaultIncidents' })
  public vaults;

  @Expose()
  public filename: string;

  @Expose()
  @ExposeRawData()
  @Exclude({ toPlainOnly: true })
  public geometry: Point;

  @Expose({ name: 'usngCoord' })
  public usngCoordinates: string;

  @Expose()
  public speed: number;

  @Expose()
  public source: string;

  @Expose()
  public altitude: number;

  @Expose()
  public camera: SnCamera;

  @Exclude({ toPlainOnly: true })
  public urls: SnRecordingUrls;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public fileIsAvailable: boolean;

  @Expose()
  public details: string;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public durationSeconds: number;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public durationString: string;

  @Expose()
  @Type(() => SnUser)
  public user: SnUser;

  @Expose()
  @Type(() => SnTeam)
  public team: SnTeam;

  @Expose()
  @Type(() => Date)
  public createdTimestamp: Date;

  @Expose({ name: 'startTimestamp' })
  @Type(() => Date)
  public startedAt: Date;

  @Expose({ name: 'endTimestamp' })
  @Type(() => Date)
  public endedAt: Date;

  @Expose({ name: 'expirationDate' })
  @Type(() => Date)
  public expiredAt: Date;

  @ExposeRawData()
  public integrationMeta;

  @Expose()
  public filesize: number;

  @Expose()
  public downloadStatus: number | string;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public isShared: boolean;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnShareInfo)
  public shareInfo: SnShareInfo;

  public readonly contentType = SnContentType.Camera;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }

  get coords(): { lng: number; lat: number } {
    if (!this.geometry) {
      return;
    }
    return {
      lng: this.geometry.coordinates[0],
      lat: this.geometry.coordinates[1],
    };
  }

  public toGeoJSON(): Feature<Point> {
    return {
      type: 'Feature',
      geometry: this.geometry,
      id: this.id,
      properties: {
        id: this.id,
        durationString: this.durationString,
        durationSeconds: this.durationSeconds,
      },
    };
  }

  public toMetaJSON(): Object {
    return {
      latitude: this.coords?.lat,
      longitude: this.coords?.lng,
      ...classToPlain(this),
    };
  }
}
